export const OPEN_GRAPH_IMAGE_WIDTH = "1200";
export const OPEN_GRAPH_IMAGE_HEIGHT = "630";

export interface SocialMetadata {
  description: string;
  documentDescription: string;
  imagePath: string;
  title: string;
}

export type SymbolOpenGraphAssetClass = "crypto" | "tradfi";

export const DEFAULT_SOCIAL_METADATA: SocialMetadata = {
  description: "Build intelligent portfolios with automated onchain execution.",
  documentDescription:
    "Glider helps you build and run intelligent portfolios with automated onchain execution.",
  imagePath: "/v1/og/general.png",
  title: "Glider | Put your capital where it belongs",
};

export const EXPLORE_SOCIAL_METADATA: SocialMetadata = {
  description:
    "Discover stocks, crypto, themes, and portfolio starting points on Glider.",
  documentDescription:
    "Discover stocks, crypto, themes, and portfolio starting points on Glider.",
  imagePath: "/v1/og/explore.png",
  title: "Explore | Glider",
};

export const STOCKS_SOCIAL_METADATA: SocialMetadata = {
  description:
    "Browse tokenized equities, curated stock strategies, and Ondo-powered stock assets on Glider.",
  documentDescription:
    "Browse tokenized equities, curated stock strategies, and Ondo-powered stock assets on Glider.",
  imagePath: "/v1/og/stocks.png",
  title: "Stocks | Glider",
};

export const CRYPTO_SOCIAL_METADATA: SocialMetadata = {
  description:
    "Browse crypto markets, themes, and portfolio starting points on Glider.",
  documentDescription:
    "Browse crypto markets, themes, and portfolio starting points on Glider.",
  imagePath: "/v1/og/crypto.png",
  title: "Crypto | Glider",
};

export const COMMODITIES_SOCIAL_METADATA: SocialMetadata = {
  description: "Own tokenized gold, silver, and uranium on Glider.",
  documentDescription: "Own tokenized gold, silver, and uranium on Glider.",
  imagePath: "/v1/og/general.png",
  title: "Commodities | Glider",
};

export const BONDS_SOCIAL_METADATA: SocialMetadata = {
  description:
    "Sovereign and credit yield from six economies, tokenized on Glider.",
  documentDescription:
    "Sovereign and credit yield from six economies, tokenized on Glider.",
  imagePath: "/v1/og/general.png",
  title: "Bonds | Glider",
};

export const PRE_IPO_SOCIAL_METADATA: SocialMetadata = {
  description:
    "Exposure to the most wanted private companies, before they list.",
  documentDescription:
    "Exposure to the most wanted private companies, before they list.",
  imagePath: "/v1/og/general.png",
  title: "Pre-IPO | Glider",
};

export const CURRENCIES_SOCIAL_METADATA: SocialMetadata = {
  description: "Hold fully reserved global currencies on Glider.",
  documentDescription: "Hold fully reserved global currencies on Glider.",
  imagePath: "/v1/og/general.png",
  title: "Currencies | Glider",
};

export const PRIVATE_CREDIT_SOCIAL_METADATA: SocialMetadata = {
  description: "Tokenized private-credit funds and lending yield on Glider.",
  documentDescription:
    "Tokenized private-credit funds and lending yield on Glider.",
  imagePath: "/v1/og/general.png",
  title: "Private credit | Glider",
};

export function createSymbolSocialMetadata(
  symbol: string,
  options?: {
    assetClass?: SymbolOpenGraphAssetClass;
    title?: string;
  },
): SocialMetadata {
  const normalizedSymbol = symbol.trim().toUpperCase();
  const imagePath = `/v1/og/symbol/${encodeURIComponent(normalizedSymbol)}.png`;
  return {
    description: `Live ${normalizedSymbol} price, market data, and onchain trading on Glider.`,
    documentDescription: `Track ${normalizedSymbol} and trade it onchain with automated portfolios on Glider.`,
    imagePath: options?.assetClass
      ? `${imagePath}?assetClass=${options.assetClass}`
      : imagePath,
    title: options?.title ?? `${normalizedSymbol} | Glider`,
  };
}

const RECEIPT_SHARE_SLUG_PATTERN = /^[a-zA-Z0-9_-]{16,64}$/;

/**
 * Public receipt links (/r/:slug) unfurl with a per-receipt card rendered by
 * the platform-api OG route (interim home until the OpenGraph-images worker
 * ships). The slug is safe to embed: it's the share secret the visitor
 * already holds by having the link.
 */
export function getReceiptShareSocialMetadata(
  pathname: string,
): SocialMetadata | null {
  const normalizedPathname = normalizePathname(pathname);
  if (!normalizedPathname.startsWith("/r/")) {
    return null;
  }
  const slug = normalizedPathname.slice("/r/".length);
  if (!RECEIPT_SHARE_SLUG_PATTERN.test(slug)) {
    return null;
  }
  return {
    description:
      "A Glider activity receipt — deposits, orders, and portfolio updates with on-chain proof.",
    documentDescription:
      "View this Glider activity receipt: status, amounts, and on-chain transactions.",
    imagePath: `/v1/og/receipt/${slug}.png`,
    title: "Receipt | Glider",
  };
}

const DISCOVERY_ROUTE_SOCIAL_METADATA_BY_PATHNAME = {
  "/bonds": BONDS_SOCIAL_METADATA,
  "/commodities": COMMODITIES_SOCIAL_METADATA,
  "/crypto": CRYPTO_SOCIAL_METADATA,
  "/currencies": CURRENCIES_SOCIAL_METADATA,
  "/explore": EXPLORE_SOCIAL_METADATA,
  "/pre-ipo": PRE_IPO_SOCIAL_METADATA,
  "/private-credit": PRIVATE_CREDIT_SOCIAL_METADATA,
  "/stocks": STOCKS_SOCIAL_METADATA,
} as const satisfies Record<string, SocialMetadata>;

type DiscoveryRoutePathname =
  keyof typeof DISCOVERY_ROUTE_SOCIAL_METADATA_BY_PATHNAME;

function normalizePathname(pathname: string): string {
  if (!pathname || pathname === "/") {
    return "/";
  }

  return pathname.endsWith("/") ? pathname.slice(0, -1) : pathname;
}

export function getDiscoveryRouteSocialMetadata(
  pathname: string,
): SocialMetadata | null {
  const normalizedPathname = normalizePathname(pathname);
  if (
    normalizedPathname !== "/crypto" &&
    normalizedPathname !== "/explore" &&
    normalizedPathname !== "/stocks"
  ) {
    return null;
  }

  const routePathname: DiscoveryRoutePathname = normalizedPathname;
  return DISCOVERY_ROUTE_SOCIAL_METADATA_BY_PATHNAME[routePathname];
}
